import { memo, useState } from 'react';
import { Badge, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle, ArrowRight, ShieldAlert } from 'lucide-react';

/**
 * Instance-level credential-expiry banner (CT-4198).
 *
 * Pixel-perfect to prototype `.gate-banner`:
 *   - `auth_expiring` → `.gate-banner-warn` (amber) + "Use with caution"
 *   - `auth_expired`  → `.gate-banner-bad`  (red)   + "Use in workflows blocked"
 *
 * Layout: [36×36 icon box] | title + chip / description | primary CTA →
 *
 * Day count is presentational formatting of `credentialExpiresAt`. CTA opens the
 * Credential Rotation Flow (CT-4194; stub via `onRotate` until it lands).
 *
 * Note: `@aimsos/ui` `StatusMessage` is a centered empty-state, not an inline
 * actionable banner — so this mirrors `.gate-banner` with `Badge` + `Button`.
 */
function AuthExpiryBanner({
  status,
  credentialExpiresAt,
  providerName,
  onRotate,
  canManage = true,
  className = '',
}) {
  const { t } = useTranslation('admin-studio');

  // Capture "now" once at mount — the banner doesn't tick, and calling Date.now()
  // during render is impure (react-hooks/purity). Hooks stay above the early return.
  const [nowMs] = useState(() => Date.now());

  if (status !== 'auth_expiring' && status !== 'auth_expired') return null;

  const isExpired = status === 'auth_expired';

  const daysToExpiry = credentialExpiresAt
    ? Math.max(0, Math.ceil((new Date(credentialExpiresAt).getTime() - nowMs) / 86_400_000))
    : 0;

  const Icon = isExpired ? ShieldAlert : AlertTriangle;

  const expiringTitleKey =
    daysToExpiry === 1
      ? 'integrations.status.authBanner.expiringTitleOne'
      : 'integrations.status.authBanner.expiringTitle';
  const title = isExpired
    ? providerName
      ? t('integrations.status.authBanner.expiredTitleNamed', { name: providerName })
      : t('integrations.status.authBanner.expiredTitle')
    : t(expiringTitleKey, { count: daysToExpiry });

  const body = isExpired
    ? t('integrations.status.authBanner.expiredBody')
    : t('integrations.status.authBanner.expiringBody');

  const chipLabel = isExpired
    ? t('integrations.status.authBanner.useBlocked')
    : t('integrations.status.authBanner.useWithCaution');

  const ctaLabel = isExpired
    ? t('integrations.status.authBanner.reauthenticateCta')
    : t('integrations.status.authBanner.rotateCta');

  return (
    <div
      role="alert"
      className={[
        'flex items-center gap-4 rounded-[10px] border px-[18px] py-[14px]',
        isExpired
          ? 'border-[color-mix(in_srgb,#EF4444_28%,transparent)] bg-[color-mix(in_srgb,#EF4444_8%,transparent)]'
          : 'border-[color-mix(in_srgb,#FDC700_28%,transparent)] bg-[color-mix(in_srgb,#FDC700_8%,transparent)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        aria-hidden="true"
        className={[
          'flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[9px]',
          isExpired
            ? 'bg-[rgba(239,68,68,0.14)] text-[#EF4444]'
            : 'bg-[rgba(253,199,0,0.14)] text-[#FDC700]',
        ].join(' ')}
      >
        <Icon size={18} strokeWidth={1.8} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-[3px] flex flex-wrap items-center gap-2.5 text-[14px] font-bold tracking-[0.01em] text-content">
          <span>{title}</span>
          <Badge
            size="small"
            radius="full"
            variant={isExpired ? 'danger' : 'warning'}
            className={[
              '!h-[18px] !gap-0 !px-[7px] !py-0 !text-[10px] !font-extrabold !uppercase !leading-none !tracking-[0.04em]',
              isExpired
                ? '!border-[rgba(239,68,68,0.36)] !bg-[rgba(239,68,68,0.18)] !text-[#EF4444]'
                : '!border-[rgba(253,199,0,0.36)] !bg-[rgba(253,199,0,0.18)] !text-[#FDC700]',
            ].join(' ')}
          >
            {chipLabel}
          </Badge>
        </div>
        <p className="text-[13px] leading-[1.5] text-content-secondary dark:text-white/60">{body}</p>
      </div>

      {/* Credential management (rotate/reauthenticate) follows the same
          `canManageIntegrations` gate as the header actions (CT-4178). */}
      {canManage ? (
        <div className="shrink-0">
          <Button
            variant="classicPrimary"
            size="sm"
            rightIcon={ArrowRight}
            onClick={onRotate}
          >
            {ctaLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default memo(AuthExpiryBanner);
